import * as S from './styles';
import * as G from '../../theme/global-styles';
import { useEffect, useRef, useState } from 'react'
import ClientLogo from '../../components/ClientLogo';
import { ThemedButton } from '../../components/ThemedButton';
import PoweredByQITech from '../../components/PoweredByQITech';
import { useThemeContext } from "../../contexts/ThemeContext";

interface LoginTermOfAuthorizationProps {
    setVisibleTerm: (term: string) => void;
    type?: 'private_consignment' | 'default';
}

export const LoginTermOfAuthorization = ({ setVisibleTerm, type }: LoginTermOfAuthorizationProps) => {

    const { clientThemeSettings } = useThemeContext();

    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
    const termEndRef = useRef(null)
    const scrollParentRef = useRef(null)


    useEffect(() => {
        const sentinel = termEndRef.current;
        if (!sentinel) return;

        const findScrollableAncestor = (el) => {
            let node = el.parentElement;
            while (node) {
                const { overflowY } = window.getComputedStyle(node);
                if (overflowY === 'auto' || overflowY === 'scroll') return node;
                node = node.parentElement;
            }
            return window;
        };

        const scrollParent = findScrollableAncestor(sentinel);
        scrollParentRef.current = scrollParent;

        const THRESHOLD = 50;
        const checkScrollEnd = () => {
            const isWindow = scrollParent === window;
            const nearBottom = isWindow
                ? window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - THRESHOLD
                : scrollParent.scrollTop + scrollParent.clientHeight >= scrollParent.scrollHeight - THRESHOLD;
            if (nearBottom) setHasScrolledToEnd(true);
        };

        scrollParent.addEventListener('scroll', checkScrollEnd, { passive: true });
        checkScrollEnd();
        return () => scrollParent.removeEventListener('scroll', checkScrollEnd);
    }, [])

 
    const handleButtonClick = () => {
        if (hasScrolledToEnd) {
            setVisibleTerm(null);
        } else {
            const sp = scrollParentRef.current;
            if (sp && sp !== window) {
                sp.scrollTo({ top: sp.scrollHeight, behavior: 'smooth' });
            } else {
                termEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    if (type === 'private_consignment') {
        return (
            <G.Container style={{padding: "16px 0"}}>
                <ClientLogo clientThemeSettings={clientThemeSettings} size="small" />
                <S.TermContainer>
                    <G.Title>Termo de Autorização</G.Title>
                    <S.Description>
                    Por meio deste Termo de Autorização de
                    Consulta de Dados, o cliente qualificado na proposta de crédito <b>("Você")</b> autoriza o MTE/DATAPREV a disponibilizar as informações abaixo indicadas para apoiar a contratação/simulação de empréstimo consignado, a fim de subsidiar a proposta pelo Banco Credor.
                    </S.Description>
                    <S.Divider />
                    <S.Subtitle>Informações a serem disponibilizadas:</S.Subtitle>
                    <S.Indent>
                        <S.UnorderedList>
                            <S.ListItem>CPF</S.ListItem>
                            <S.ListItem>Matrícula</S.ListItem>
                            <S.ListItem>Inscrição do Empregador (Código e Descrição)</S.ListItem>
                            <S.ListItem>Número da Inscrição do Empregador</S.ListItem>
                            <S.ListItem>Nome</S.ListItem>
                            <S.ListItem>Sexo (Código e Descrição)</S.ListItem>
                            <S.ListItem>Data de Nascimento</S.ListItem>
                            <S.ListItem>Código da Categoria do Trabalhador</S.ListItem>
                            <S.ListItem>Elegibilidade (Sim/Não)</S.ListItem>
                            <S.ListItem>Valor Total dos Vencimentos</S.ListItem>
                            <S.ListItem>Valor Base da Margem</S.ListItem>
                            <S.ListItem>Valor da Margem Disponível</S.ListItem>
                            <S.ListItem>Data de Admissão</S.ListItem>
                            <S.ListItem>Data de Desligamento</S.ListItem>
                            <S.ListItem>Código do Motivo do Desligamento</S.ListItem>
                            <S.ListItem>Pessoa Exposta Politicamente (Código e Descrição)</S.ListItem>
                            <S.ListItem>Alertas de Afastamento e Aviso Prévio</S.ListItem>
                        </S.UnorderedList>
                    </S.Indent>
                    <S.Divider />
                    <S.Description>
                    Este termo autoriza esta Instituição Financeira a consultar as informações acima descritas durante um período de 30 dias. Este pedido poderá ser efetuado pela Instituição Financeira em até 45 dias após a assinatura deste instrumento.
                    </S.Description>
                    <div ref={termEndRef} />
                </S.TermContainer>
                <S.Footer>
                    <ThemedButton
                        clientThemeSettings={clientThemeSettings}
                        loading={false}
                        onClick={handleButtonClick}
                    >
                        Estou ciente
                    </ThemedButton>
                    <PoweredByQITech />
                </S.Footer>
            </G.Container>
        );
    }
    return (
        <G.Container style={{padding: "16px 0"}}>
            <ClientLogo clientThemeSettings={clientThemeSettings} size="small" />
            <S.TermContainer>
                <G.Title>Termo de Autorização de Consulta de Dados</G.Title>
                <S.Description>
                    Por meio deste Termo de Autorização de
                    Consulta de Dados, o cliente qualificado na proposta de crédito <b>("Você")</b> autoriza o <b>INSS/DATAPREV</b> a disponibilizar as informações abaixo para fins de contratação de empréstimo consignado junto à <b>QI SOCIEDADE DE CREDITO DIRETO S.A.</b>.
                </S.Description>
                <S.Divider />
                <S.Subtitle>Dados de Identificação:</S.Subtitle>
                <S.Indent>
                    <S.UnorderedList>
                        <S.ListItem>CPF</S.ListItem>
                        <S.ListItem>Data de nascimento</S.ListItem>
                        <S.ListItem>Nome completo</S.ListItem>
                    </S.UnorderedList>
                </S.Indent>
                <S.Divider />
                <S.Subtitle>Dados do Benefício:</S.Subtitle>
                <S.Indent>
                    <S.UnorderedList>
                        <S.ListItem>Número do benefício</S.ListItem>
                        <S.ListItem>Situação do benefício</S.ListItem>
                        <S.ListItem>Indicação de que o benefício foi concedido por liminar</S.ListItem>
                        <S.ListItem>Data de Cessação do Benefício - DCB (se houver)</S.ListItem>
                        <S.ListItem>Possui representante legal</S.ListItem>
                        <S.ListItem>Possui procurador</S.ListItem>
                        <S.ListItem>Possui entidade de representação</S.ListItem>
                        <S.ListItem>Pensão alimentícia</S.ListItem>
                        <S.ListItem>Bloqueado para empréstimo</S.ListItem>
                        <S.ListItem>Data da última perícia médica</S.ListItem>
                        <S.ListItem>Data do despacho do benefício - DDB</S.ListItem>
                        <S.ListItem>Possui entidade</S.ListItem>
                        <S.ListItem>Valor total comprometido</S.ListItem>
                        <S.ListItem>Valor máximo de comprometimento permitido</S.ListItem>
                        <S.ListItem>Dados do Pagamento do Benefício:</S.ListItem>
                        <S.ListItem>UF onde o beneficiário recebe os proventos</S.ListItem>
                        <S.ListItem>Tipo de crédito (cartão ou conta-corrente)</S.ListItem>
                        <S.ListItem>Agência pagadora</S.ListItem>
                        <S.ListItem>Indicação da instituição financeira que paga o benefício</S.ListItem>
                        <S.ListItem>Conta-corrente onde o benefício é pago</S.ListItem>
                        <S.ListItem>Margem consignável disponível</S.ListItem>
                        <S.ListItem>Margem consignável disponível para Cartão</S.ListItem>
                        <S.ListItem>Valor limite para cartão</S.ListItem>
                        <S.ListItem>Quantidade de empréstimos ativos/suspensos</S.ListItem>
                    </S.UnorderedList>
                </S.Indent>
                <S.Divider />
                <S.Description>
                    Este Termo autoriza a <b>QI SOCIEDADE DE CREDITO DIRETO S.A.</b> consultar suas informações durante o período de até 30 (trinta) dias. Este pedido poderá ser efetuado pela <b>QI SOCIEDADE DE CREDITO DIRETO S.A.</b> até 45 (quarenta e cinco) dias após a assinatura deste instrumento.
                </S.Description>
                <S.Description>
                    Você reconhece a validade da assinatura eletrônica que utilizou para aceitar as condições deste Termo (coleta de seus dados biométricos e/ou aceite eletrônico, a depender do sistema ou plataforma utilizados), nos termos do <b>parágrafo 2º do artigo 10 da Medida Provisória nº 2.200/01</b>.
                </S.Description>
                <div ref={termEndRef} />
            </S.TermContainer>
            <S.Footer>
                <ThemedButton
                    clientThemeSettings={clientThemeSettings}
                    onClick={handleButtonClick}
                >
                    Estou ciente
                </ThemedButton>
                <PoweredByQITech />
            </S.Footer>
        </G.Container>
    );

};